/* istanbul ignore file */
import React from 'react';
import { ShowSection, SectionRow, BigSectionRow, ShowPageLayout } from 'framework/ui/layout';
import { useAccountResourceService } from 'domain/shared/account';
import ShowAccountPageMenu from 'domain/sales/account/Show/ShowAccountPageMenu';
import AccountEntityHeader from 'domain/sales/account/shared/AccountEntityHeader';
import SingleAccountBreadcrumbs from 'domain/sales/account/shared/SingleAccountBreadcrumbs';
import { useAccountId } from 'domain/sales/account/accountRouting';
import { AddressField, HtmlField, PhoneField, TextField, useFieldHelper } from 'framework/ui/field';
import AccountTypeField from 'domain/sales/account/shared/field/AccountTypeField';
import IndustryField from 'domain/sales/shared/field/IndustryField';
import { AttachmentsListField } from 'domain/shared/artifact/field';
import EditAccountButton from 'domain/sales/account/shared/Button/EditAccountButton';
import { DeleteButton } from 'framework/ui/ui-builder';
import { AccountLinkField, useAccountUrl } from 'domain/shared/account';
import LitigationHoldRow from 'domain/sales/account/shared/field/TicketRetentionOnHoldField';
import { LeadLinkField } from 'domain/shared/lead';
import { HTTP } from 'framework/api';
import { useAuthorization } from 'framework/authorization';
import { ReplaceUrlWithLink } from 'framework/utils';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';
import { READ_SENSITIVE_ACCOUNT_NAME, UPDATE_ACCOUNT } from '../model';

const AccountViewPage = ({ isSensitiveDataIsShown = false, toggleSensitiveDataButton = <></> }) => {
  const { deleteById } = useAccountResourceService();
  const { getListUrl } = useAccountUrl();
  const id = useAccountId();
  const { isGranted } = useAuthorization();
  const { isFeatureEnabled } = useFeatureToggle();
  const { getValue: getTitle } = useFieldHelper({ source: isSensitiveDataIsShown ? 'realAccountName' : 'accountName' });

  const buttons = [
    isGranted(READ_SENSITIVE_ACCOUNT_NAME) && toggleSensitiveDataButton,
    <EditAccountButton permissionToCheck={UPDATE_ACCOUNT} />,
    <DeleteButton
      deleteMethod={() => deleteById(id)}
      displayMessage="Do you really want to delete this account?"
      afterDelete={{
        redirectUrl: getListUrl(),
        successMessage: 'The Account has been successfully deleted.',
        errorMessage: `Can't delete account`,
        errorStatusesMessages: {
          [HTTP.BAD_REQUEST]: 'The Account cannot be deleted as it has related Opportunity or Contact',
        },
      }}
      permissionToCheck={UPDATE_ACCOUNT}
    />,
  ].filter(Boolean) as React.ReactElement[];

  return (
    <ShowPageLayout
      headerButtons={buttons}
      menu={<ShowAccountPageMenu />}
      entityHeader={<AccountEntityHeader entityTitle={getTitle() || '-'} />}
      breadcrumbs={<SingleAccountBreadcrumbs />}
      hasSeparateSections
    >
      <ShowSection title="Account information">
        <SectionRow>
          <PhoneField label="Phone" source="phoneNumber" />
          <AccountTypeField label="Account type" source="accountType" />
          <ReplaceUrlWithLink>
            <HtmlField label="Website" source="website" />
          </ReplaceUrlWithLink>
          <IndustryField label="Industry" source="industry" />
          <TextField label="No. of workers" source="employeesNumber" />
          <AccountLinkField label="Parent account" idSource="parentAccountId" source="parentAccountName" />
          <TextField label="Account owner" source="ownerName" />
          <LeadLinkField
            label="Converted from"
            idSource="convertedFromId"
            nameSource="convertedFromName"
            isVisible={(data: any) => data.getValueBySource('convertedFromId')}
          />
          <TextField label="Account ID" source="accountId" />
        </SectionRow>

        {isFeatureEnabled(Features.ACCOUNT_TICKET_RETENTION_ON_HOLD) && <LitigationHoldRow />}
      </ShowSection>

      <ShowSection title="Address information">
        <BigSectionRow>
          <AddressField source="address" />
        </BigSectionRow>
      </ShowSection>

      <ShowSection title="Description information">
        <BigSectionRow>
          <ReplaceUrlWithLink>
            <HtmlField label="" source="accountDescription" />
          </ReplaceUrlWithLink>
        </BigSectionRow>
      </ShowSection>

      <ShowSection title="Attachments">
        <BigSectionRow>
          <AttachmentsListField label="" source="attachmentsIds" />
        </BigSectionRow>
      </ShowSection>
    </ShowPageLayout>
  );
};

export default AccountViewPage;
